import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Chip, Divider, LinearProgress, Paper, Stack, Typography,
} from '@mui/material';
import { pct, px, STATE_COLORS } from '../utils/dynamanSimulation';

/**
 * The step-by-step HUD: where the machine is, what it just saw, and what the premise is worth.
 *
 * <h2>The state chip is the largest thing on screen</h2>
 *
 * Everything else here is context for it. The question a stepped run answers is "what phase is this
 * in and what moved it", and a state rendered at body-text size among a dozen other numbers is a
 * state nobody watching a replay can follow.
 *
 * <h2>The transition log is newest-first and short</h2>
 *
 * A viewer is watching bars go past; the thing they want is what JUST happened, not the beginning
 * of a run they have already seen. Appending downward would also mean the interesting line moves
 * further away with every event, so the list is prepended and capped.
 *
 * <h2>"Unrealised" is on the PnL, every time it is shown</h2>
 *
 * The HUD's PnL is a mark against the current bar's close. Labelling it once at the top would be
 * enough for a careful reader and not enough for anyone glancing at a moving number.
 */

const Metric = ({ label, value, tone }) => (
  <Box sx={{ minWidth: 104 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: tone, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

Metric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.string,
};

const DynaManLiveHud = ({
  bar = null, transitions = [], streaming = false, rejectionCount = 0,
}) => {
  const state = bar?.state || 'CASH';
  const trade = bar?.trade;
  const progress = bar && bar.total ? (bar.index / bar.total) * 100 : 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={state}
          color={STATE_COLORS[state] || 'default'}
          sx={{ fontWeight: 700, fontSize: '1rem', height: 36, px: 1 }}
        />
        {bar ? (
          <Typography variant="body2" color="text.secondary">
            bar {bar.index.toLocaleString()} of {bar.total.toLocaleString()} · {bar.timeframe} ·{' '}
            {String(bar.time).replace('T', ' ').slice(0, 16)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {streaming ? 'Warming the engine…' : 'Not running.'}
          </Typography>
        )}
        {rejectionCount > 0 && (
          <Chip size="small" variant="outlined" color="info"
            label={`${rejectionCount} signals declined`} />
        )}
      </Stack>

      <LinearProgress
        variant={bar ? 'determinate' : 'indeterminate'}
        value={progress}
        sx={{ mb: 2, visibility: streaming || bar ? 'visible' : 'hidden' }}
      />

      {bar && (
        <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Metric label="Open" value={px(bar.open)} />
          <Metric label="High" value={px(bar.high)} />
          <Metric label="Low" value={px(bar.low)} />
          <Metric label="Close" value={px(bar.close)} />
          <Metric label="Volume" value={bar.volume?.toLocaleString?.() ?? bar.volume} />
        </Stack>
      )}

      <Divider sx={{ mb: 2 }} />

      {trade ? (
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', gap: 2 }}>
          <Metric label="Direction" value={trade.direction} />
          <Metric label="Entry" value={px(trade.entryPrice)} />
          <Metric label="Stop" value={px(trade.structuralStop)} tone="error.main" />
          <Metric
            label="Trailing floor"
            value={trade.trailingFloor === null || trade.trailingFloor === undefined ? '—' : px(trade.trailingFloor)}
            tone="warning.main"
          />
          <Metric label="High-water" value={px(trade.highWaterMark)} />
          <Metric
            label="Open PnL (unrealised)"
            value={pct(trade.openPnlPercent)}
            tone={trade.openPnlPercent >= 0 ? 'success.main' : 'error.main'}
          />
          <Metric
            label="Open R (unrealised)"
            value={trade.openR.toFixed(2)}
            tone={trade.openR >= 0 ? 'success.main' : 'error.main'}
          />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No capital deployed.
        </Typography>
      )}

      {transitions.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">Recent transitions</Typography>
          <Box
            sx={{
              mt: 0.5, p: 1, borderRadius: 1, bgcolor: 'action.hover',
              fontFamily: 'monospace', fontSize: '0.75rem', maxHeight: 140, overflowY: 'auto',
            }}
          >
            {transitions.map((t, i) => (
              <div key={`${t.time}-${t.to}-${i}`}>
                {String(t.time).replace('T', ' ').slice(0, 16)} [{t.timeframe}] {t.from} → {t.to}
                {t.reason ? ` — ${t.reason}` : ''}
              </div>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

DynaManLiveHud.propTypes = {
  bar: PropTypes.shape({
    index: PropTypes.number,
    total: PropTypes.number,
    time: PropTypes.string,
    timeframe: PropTypes.string,
    open: PropTypes.number,
    high: PropTypes.number,
    low: PropTypes.number,
    close: PropTypes.number,
    volume: PropTypes.number,
    state: PropTypes.string,
    trade: PropTypes.object,
  }),
  transitions: PropTypes.arrayOf(PropTypes.object),
  streaming: PropTypes.bool,
  rejectionCount: PropTypes.number,
};

export default DynaManLiveHud;
